// src/config.js

/**
 * Pure helper: resolve the WebSocket base URL for a given environment.
 * Exported for testability (import.meta.env cannot be easily mocked in Vitest).
 *
 * Priority: explicitUrl > useLocalApi (null) > deployEnv mapping
 *
 * @param {string} deployEnv  - 'staging' | 'beta' | 'production' | 'development'
 * @param {boolean} useLocalApi
 * @param {string|undefined} explicitUrl - VITE_WS_URL override
 * @returns {string|null} WebSocket base URL (may or may not include /notifications)
 */
export function getWebSocketUrl(deployEnv, useLocalApi, explicitUrl) {
  // Highest-priority explicit override
  if (explicitUrl) return explicitUrl;

  // Local API mode disables WebSocket
  if (useLocalApi) return null;

  const envMap = {
    staging: 'wss://stage-ws.shieldshot.com/notifications',
    beta: 'wss://beta-ws.shieldshot.com/notifications',
    production: 'wss://ws.shieldshot.com/notifications',
  };

  return envMap[deployEnv] || null; // development/unmatched → null
}

/**
 * Pure helper to determine store URL based on deployment environment.
 * Follows the same pattern as getWebSocketUrl for testability.
 * @param {string} deployEnv - The deployment environment ('staging', 'beta', 'production')
 * @param {string} [explicitUrl] - Optional explicit URL override (VITE_STORE_URL)
 * @returns {string} The store URL
 */
export function getStoreUrl(deployEnv, explicitUrl) {
  if (explicitUrl) return explicitUrl;
  const storeMap = {
    staging: 'https://staging-store.shieldshot.com',
    beta: 'https://beta-store.shieldshot.com',
    production: 'https://store.shieldshot.com',
  };
  return storeMap[deployEnv] || storeMap.production;
}

/**
 * Pure helper to determine the watermark API URL based on the deployment environment.
 * @param {string} deployEnv - The deployment environment ('staging', 'beta', 'production', 'development')
 * @param {boolean} useLocalApi - Whether the frontend is using a local API
 * @param {string} [apiUrl] - Primary platform API URL override/fallback for local mode
 * @param {string} [explicitUrl] - Optional explicit URL override (VITE_WATERMARK_API_URL)
 * @returns {string} The watermark API URL
 */
export function getWatermarkApiUrl(deployEnv, useLocalApi, apiUrl, explicitUrl) {
  if (explicitUrl) {
    // Strip trailing /v1 to avoid double-prefixing with hardcoded /v1/ in
    // fetch paths throughout settingsWatermark.jsx, useStoreWatermarkApply.js,
    // and StoreManager.jsx. The GHA deploy variable may include the base path;
    // client code appends its own /v1/ prefix.
    return explicitUrl.replace(/\/v1\/?$/, '');
  }
  if (useLocalApi) return apiUrl || 'http://localhost:3001';
  const watermarkMap = {
    staging: 'https://stage-api-wm.shieldshot.com',
    beta: 'https://beta-api-wm.shieldshot.com',
    production: 'https://api-wm.shieldshot.com',
  };
  return watermarkMap[deployEnv] || watermarkMap.production;
}

// SS-242: Feature flag for reporting dashboard endpoint cutover.
// Default: enabled (reporting primary). Set VITE_REPORTING_DASHBOARD_ENABLED='false' to disable.
const REPORTING_DASHBOARD_ENABLED = import.meta.env.VITE_REPORTING_DASHBOARD_ENABLED !== 'false';

const DEPLOY_ENV = import.meta.env.VITE_DEPLOY_ENV || "development";
const USE_LOCAL_API = import.meta.env.VITE_USE_LOCAL_API === "true";
let API_URL = import.meta.env.VITE_API_URL || "https://api.shieldshot.com";
let WS_URL = getWebSocketUrl(DEPLOY_ENV, USE_LOCAL_API, import.meta.env.VITE_WS_URL);
let GRAPHQL_URL = import.meta.env.VITE_GRAPHQL_URL || "https://stage-query.shieldshot.com/graphql";
const STORE_URL = getStoreUrl(DEPLOY_ENV, import.meta.env.VITE_STORE_URL);

let WATERMARK_API_URL = getWatermarkApiUrl(DEPLOY_ENV, USE_LOCAL_API, API_URL, import.meta.env.VITE_WATERMARK_API_URL);

let AVATARS_BUCKET;
let UPLOADS_BUCKET;
let TEMP_BUCKET;

if (DEPLOY_ENV === "production") {
  AVATARS_BUCKET = "shieldshot-avatars";
  UPLOADS_BUCKET = "shieldshot-uploads";
  TEMP_BUCKET = "shieldshot-temp";
} else if (USE_LOCAL_API) {
  API_URL = "http://localhost:3001"; // Change to your local API port if needed
  AVATARS_BUCKET = "shieldshot-avatars-stage";
  UPLOADS_BUCKET = "shieldshot-uploads-stage";
  TEMP_BUCKET = "shieldshot-temp-stage";
} else if (DEPLOY_ENV === "staging") {
  AVATARS_BUCKET = "shieldshot-avatars-stage";
  UPLOADS_BUCKET = "shieldshot-uploads-stage";
  TEMP_BUCKET = "shieldshot-temp-stage";
} else if (DEPLOY_ENV === "beta") {
  AVATARS_BUCKET = "shieldshot-avatars-beta";
  UPLOADS_BUCKET = "shieldshot-uploads-beta";
  TEMP_BUCKET = "shieldshot-temp-beta";
}

export { API_URL, WS_URL, AVATARS_BUCKET, UPLOADS_BUCKET, TEMP_BUCKET, GRAPHQL_URL, STORE_URL, WATERMARK_API_URL, REPORTING_DASHBOARD_ENABLED };
